import { createContext, useContext, useEffect, useState } from 'react';
import { noop } from 'lodash';

import type { GalleryImageWithTitle } from 'Components/Gallery/MainGallery/MainGallery';
import { IMAGE_KIND } from 'Pages/vehicles/helpers/images.const';
import { BODY_TYPE } from 'Pages/vehicles/helpers/images.helpers';
import { EXPERIMENTS } from 'Services/optimizely/optimizely.consts';
import { useOptimizelyDecision } from 'Services/optimizely/useOptimizelyDecision';

interface GalleryDataProviderProps {
	bodyType?: string;
	categories?: Record<string, number>;
	children: React.ReactNode;
	images: GalleryImageWithTitle[];
}

export interface GalleryDataProviderState {
	bodyType: string;
	categories: Record<string, number>;
	images: GalleryImageWithTitle[];
	setImages: (images: GalleryImageWithTitle[]) => void;
}

export const GalleryDataContext = createContext<GalleryDataProviderState>({
	bodyType: BODY_TYPE.HATCHBACK_TYPE,
	categories: {},
	images: [],
	setImages: noop,
});

const filterAdditionalImages = (images: GalleryImageWithTitle[], shouldFilter: boolean) => {
	return images.filter((image) => (shouldFilter ? image : image.kind !== IMAGE_KIND.additionalImage));
};

export const GalleryDataProvider: React.FC<GalleryDataProviderProps> = ({
	bodyType = BODY_TYPE.HATCHBACK_TYPE,
	categories = {},
	children,
	images,
}) => {
	const decision = useOptimizelyDecision({ experimentId: EXPERIMENTS.ADDITIONAL_PHOTOS });
	const showAdditionalPhotos = decision.isEnabled && decision.decision === 'on';

	const [finalImages, setFinalImages] = useState<GalleryImageWithTitle[]>(
		filterAdditionalImages(images, showAdditionalPhotos),
	);

	useEffect(() => {
		setFinalImages(filterAdditionalImages(images, showAdditionalPhotos));
	}, [images, showAdditionalPhotos]);

	return (
		<GalleryDataContext.Provider value={{ bodyType, categories, images: finalImages, setImages: setFinalImages }}>
			{children}
		</GalleryDataContext.Provider>
	);
};

export const useGallerySetImages = (): GalleryDataProviderState['setImages'] => {
	const { setImages } = useContext(GalleryDataContext);
	if (!setImages) {
		throw new Error('useGallerySetImages must be used inside GalleryProvider');
	}
	return setImages;
};
